/**
 * ProfileWidget — „Karta profilu" wg Figmy „08.6": baner, avatar z inicjałami i zieloną kropką
 * „aktywny", nazwa, firma, e-mail, odznaki (Super-user / Aktywny), trzy liczby (firmy, dokumenty,
 * ostatnio) oraz przyciski „Moje decyzje" i „Edytuj profil". Dane: sesja (`me`) + `profile-stats`.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/Badge';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useActiveCompanyScope } from '@/lib/use-active-company';

import { classifyDataError, fetchProfileStats } from '../dashboard-api';
import type { WidgetRenderProps } from '../types';
import { relativeTime } from '../widget-ui';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0]![0]! + (parts.length > 1 ? parts[parts.length - 1]![0]! : '')).toUpperCase();
}

export function ProfileWidget({ widget }: WidgetRenderProps) {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const { isAll, mode, companyId } = useActiveCompanyScope();
  const blocked = mode === 'none';

  const query = useQuery({
    queryKey: ['tenant', 'dashboard', 'profile-stats', companyId ?? 'all'],
    queryFn: () => fetchProfileStats(isAll ? 'all' : 'company'),
    enabled: widget.type === 'profile' && !blocked && me !== null,
    staleTime: 60_000,
    retry: false,
  });

  if (widget.type !== 'profile') return null;
  if (!me) return <WidgetEmpty message={t('tenant.dashboard.notLoaded')} />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );

  const name = me.user.displayName || me.user.email;
  const stats = query.data;

  return (
    <div className="flex h-full flex-col items-center gap-2 text-center">
      <div className="-mx-5 -mt-4 h-12 w-[calc(100%+2.5rem)] shrink-0 bg-accent-100 dark:bg-accent-900/30" />
      <div className="relative -mt-9 shrink-0">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border-4 border-bg-elevated bg-accent-100 text-lg font-semibold text-accent-700 dark:bg-accent-900 dark:text-accent-200">
          {initials(name)}
        </span>
        <span
          className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border-2 border-bg-elevated bg-green-500"
          role="img"
          aria-label={t('tenant.dashboard.widgets.profile.active')}
        />
      </div>
      <div className="min-w-0 max-w-full">
        <div className="truncate text-sm font-semibold text-text-primary" title={name}>
          {name}
        </div>
        <div className="truncate text-xs text-text-tertiary">{me.tenant.name}</div>
        <a
          href={`mailto:${me.user.email}`}
          className="block truncate text-xs text-accent-600 hover:underline"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {me.user.email}
        </a>
      </div>
      <div className="flex flex-wrap justify-center gap-1.5">
        {me.user.isSuperUser && (
          <Badge tone="danger" size="sm">
            {t('tenant.dashboard.widgets.profile.superUser')}
          </Badge>
        )}
        <Badge tone="success" size="sm">
          {t('tenant.dashboard.widgets.profile.active')}
        </Badge>
      </div>

      {query.isLoading ? (
        <WidgetLoading />
      ) : (
        <dl className="grid w-full grid-cols-3 gap-2 border-t border-border-secondary pt-3">
          <div>
            <dt className="text-[11px] text-text-tertiary">
              {t('tenant.dashboard.widgets.profile.companies')}
            </dt>
            <dd className="text-sm font-semibold tabular-nums text-text-primary">
              {stats?.companies ?? me.companies.length}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] text-text-tertiary">
              {t('tenant.dashboard.widgets.profile.documents')}
            </dt>
            <dd className="text-sm font-semibold tabular-nums text-text-primary">
              {(stats?.documents ?? 0).toLocaleString('pl-PL')}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] text-text-tertiary">
              {t('tenant.dashboard.widgets.profile.lastLogin')}
            </dt>
            <dd className="truncate text-sm font-semibold text-text-primary">
              {stats?.lastLoginAt
                ? relativeTime(stats.lastLoginAt)
                : t('tenant.dashboard.widgets.profile.never')}
            </dd>
          </div>
        </dl>
      )}

      <div className="mt-auto grid w-full grid-cols-2 gap-2 pt-1">
        <Link
          to="/processes/decisions/pending"
          onMouseDown={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center rounded-lg border border-border-secondary px-2 py-1.5 text-xs font-medium text-text-primary hover:bg-bg-secondary"
        >
          {t('tenant.dashboard.widgets.profile.decisions')}
        </Link>
        <Link
          to="/profile"
          onMouseDown={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center rounded-lg bg-accent-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-accent-700"
        >
          {t('tenant.dashboard.widgets.profile.editProfile')}
        </Link>
      </div>
    </div>
  );
}
